import React, { useState, useRef, useEffect } from 'react';
import {
  Github,
  Linkedin,
  Instagram,
  Mail,
  Calendar,
  MessageCircle,
  Briefcase,
  GraduationCap,
  Code,
  CheckCircle,
  AlertCircle,
  Loader2,
  Globe,
  ChevronDown,
  ArrowUpRight,
  ArrowLeft,
} from 'lucide-react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';

// ─── TRADUÇÕES ────────────────────────────────────────────────────────────────
const translations = {
  pt: {
    nav: {
      about: 'Sobre mim',
      experience: 'Experiência',
      social: 'Sociais',
      contact: 'Entre em contato',
    },
    hero: {
      label: 'Sobre mim',
      title: ['Desenvolvedor', 'Fullstack'],
      available: 'Disponível para novos projetos',
      bio:
        'Sou estudante de Engenharia de Software com foco em desenvolvimento backend, especializado na criação de APIs RESTful, modelagem e integração com bancos de dados relacionais. ' +
        'Tenho experiência prática utilizando Node.js e Spring Boot, aplicando boas práticas de arquitetura em camadas (Controller, Service e Repository), organização de código e separação de responsabilidades. ' +
        'Busco sempre desenvolver soluções bem estruturadas, escaláveis e seguras, com atenção especial à modelagem de dados, regras de negócio e integração entre sistemas.',
    },
    experience: {
      title: 'Minha Trajetória',
      tabs: { profissional: 'Profissional', academicos: 'Acadêmicos', projetos: 'Projetos' },
      viewProject: 'Ver projeto',
      viewAll: 'Ver todos os projetos',
      data: {
        profissional: [
          {
            title: 'Gerente de E-commerce',
            company: 'Mundial Peças',
            period: '2022 - Presente',
            description:
              '● Suporte técnico a desktops e sistemas internos.\n● Atendimento ao Cliente.\n● Postagem de anúncios.\n● Edição de Fotos.\n● Administração de campanhas publicitárias.\n● Realizar o processo de faturamento e transmissão eletrônica de arquivos XML da NFe.\n● Executar backups.\n● Automação de Tarefas.',
          },
        ],
        academicos: [
          {
            title: 'Bacharelado em Engenharia de Software',
            company: 'Uniamérica',
            period: '2023 - 2026',
            description:
              'Formação focada na construção de soluções robustas e escaláveis, abrangendo arquitetura de software, modelagem de sistemas, banco de dados, metodologias ágeis, inovação, cibersegurança e fundamentos de infraestrutura.\n\nDurante a graduação, desenvolvo a capacidade de transformar requisitos em sistemas bem estruturados, aplicando boas práticas de engenharia, organização de código e foco em qualidade e manutenção.',
          },
        ],
      },
    },
    social: {
      title: 'Acompanhe no LinkedIn',
      subtitle: 'Compartilho minha trajetória profissional. Confira meu perfil para os posts mais recentes.',
      viewProfile: 'Ver perfil completo',
    },
    contact: {
      title: ['Vamos', 'Conversar?'],
      subtitle: 'Escolha a melhor forma para nos conectarmos. Estou disponível para propostas e parcerias.',
      emailLabel: 'E-mail',
      whatsappLabel: 'Whatsapp',
      sendEmail: 'Enviar E-mail',
      scheduleMeeting: 'Marcar Reunião',
      form: {
        emailField: 'Email',
        subjectField: 'Assunto',
        subjectPlaceholder: 'Assunto do contato',
        messageField: 'Sua Mensagem',
        messagePlaceholder: 'Olá, gostaria de falar sobre...',
        submitIdle: 'Enviar agora',
        submitLoading: 'Enviando...',
        submitSuccess: 'Enviado!',
        successMsg: 'Mensagem enviada com sucesso! Em breve entrarei em contato.',
        errorMsg: 'Ocorreu um erro ao enviar. Tente novamente ou use o WhatsApp.',
      },
      meeting: {
        title: 'Agende uma conversa',
        subtitle: 'Entre em contato pelo WhatsApp ou e-mail para combinarmos um horário que funcione para os dois.',
        whatsappBtn: 'WhatsApp',
        emailBtn: 'Enviar E-mail',
      },
    },
    footer: {
      rights: '© 2026 - Todos os direitos reservados Pedro Soares.',
      privacy: 'Privacidade',
      terms: 'Termos',
    },
    projectsPage: {
      title: 'Todos os Projetos',
      subtitle: 'Uma seleção completa dos meus trabalhos e projetos desenvolvidos.',
      back: 'Voltar ao início',
      empty: 'Nenhum projeto cadastrado ainda.',
    },
  },

  en: {
    nav: {
      about: 'About me',
      experience: 'Experience',
      social: 'Social',
      contact: 'Get in touch',
    },
    hero: {
      label: 'About me',
      title: ['Fullstack', 'Developer'],
      available: 'Available for new projects',
      bio:
        'I am a Software Engineering student focused on backend development, specialized in building RESTful APIs, data modeling and relational database integration. ' +
        'I have hands-on experience with Node.js and Spring Boot, applying layered architecture best practices (Controller, Service and Repository), clean code organization and separation of concerns. ' +
        'I always strive to build well-structured, scalable and secure solutions, with special attention to data modeling, business rules and system integration.',
    },
    experience: {
      title: 'My Journey',
      tabs: { profissional: 'Professional', academicos: 'Academic', projetos: 'Projects' },
      viewProject: 'View project',
      viewAll: 'View all projects',
      data: {
        profissional: [
          {
            title: 'E-commerce Manager',
            company: 'Mundial Peças',
            period: '2022 - Present',
            description:
              '● Technical support for desktops and internal systems.\n● Customer service.\n● Listing and publishing ads.\n● Photo editing.\n● Advertising campaign management.\n● Invoicing and electronic transmission of NFe XML files.\n● Backup execution.\n● Task automation.',
          },
        ],
        academicos: [
          {
            title: "Bachelor's Degree in Software Engineering",
            company: 'Uniamérica',
            period: '2023 - 2026',
            description:
              'Degree focused on building robust and scalable solutions, covering software architecture, systems modeling, databases, agile methodologies, innovation, cybersecurity and infrastructure fundamentals.\n\nThroughout the program, I develop the ability to transform requirements into well-structured systems, applying engineering best practices, code organization and a focus on quality and maintainability.',
          },
        ],
      },
    },
    social: {
      title: 'Follow me on LinkedIn',
      subtitle: 'I share my professional journey. Check out my profile for the latest posts.',
      viewProfile: 'View full profile',
    },
    contact: {
      title: ["Let's", 'Talk?'],
      subtitle: "Choose the best way to connect. I'm available for proposals and partnerships.",
      emailLabel: 'E-mail',
      whatsappLabel: 'Whatsapp',
      sendEmail: 'Send Email',
      scheduleMeeting: 'Schedule Meeting',
      form: {
        emailField: 'Email',
        subjectField: 'Subject',
        subjectPlaceholder: 'Contact subject',
        messageField: 'Your Message',
        messagePlaceholder: 'Hi, I would like to talk about...',
        submitIdle: 'Send now',
        submitLoading: 'Sending...',
        submitSuccess: 'Sent!',
        successMsg: 'Message sent successfully! I will get back to you soon.',
        errorMsg: 'An error occurred while sending. Please try again or use WhatsApp.',
      },
      meeting: {
        title: 'Schedule a conversation',
        subtitle: 'Reach out via WhatsApp or email and we can find a time that works for both of us.',
        whatsappBtn: 'WhatsApp',
        emailBtn: 'Send Email',
      },
    },
    footer: {
      rights: '© 2026 - All rights reserved Pedro Soares.',
      privacy: 'Privacy',
      terms: 'Terms',
    },
    projectsPage: {
      title: 'All Projects',
      subtitle: 'A complete selection of my work and developed projects.',
      back: 'Back to home',
      empty: 'No projects registered yet.',
    },
  },

  es: {
    nav: {
      about: 'Sobre mí',
      experience: 'Experiencia',
      social: 'Redes',
      contact: 'Contáctame',
    },
    hero: {
      label: 'Sobre mí',
      title: ['Desarrollador', 'Fullstack'],
      available: 'Disponible para nuevos proyectos',
      bio:
        'Soy estudiante de Ingeniería de Software con enfoque en desarrollo backend, especializado en la creación de APIs RESTful, modelado e integración con bases de datos relacionales. ' +
        'Tengo experiencia práctica utilizando Node.js y Spring Boot, aplicando buenas prácticas de arquitectura en capas (Controller, Service y Repository), organización de código y separación de responsabilidades. ' +
        'Busco siempre desarrollar soluciones bien estructuradas, escalables y seguras, con especial atención al modelado de datos, reglas de negocio e integración entre sistemas.',
    },
    experience: {
      title: 'Mi Trayectoria',
      tabs: { profissional: 'Profesional', academicos: 'Académico', projetos: 'Proyectos' },
      viewProject: 'Ver proyecto',
      viewAll: 'Ver todos los proyectos',
      data: {
        profissional: [
          {
            title: 'Gerente de E-commerce',
            company: 'Mundial Peças',
            period: '2022 - Presente',
            description:
              '● Soporte técnico a desktops y sistemas internos.\n● Atención al Cliente.\n● Publicación de anuncios.\n● Edición de Fotos.\n● Administración de campañas publicitarias.\n● Realizar el proceso de facturación y transmisión electrónica de archivos XML de la NFe.\n● Ejecutar copias de seguridad.\n● Automatización de tareas.',
          },
        ],
        academicos: [
          {
            title: 'Licenciatura en Ingeniería de Software',
            company: 'Uniamérica',
            period: '2023 - 2026',
            description:
              'Formación enfocada en la construcción de soluciones robustas y escalables, abarcando arquitectura de software, modelado de sistemas, bases de datos, metodologías ágiles, innovación, ciberseguridad y fundamentos de infraestructura.\n\nDurante la carrera, desarrollo la capacidad de transformar requisitos en sistemas bien estructurados, aplicando buenas prácticas de ingeniería, organización del código y enfoque en calidad y mantenimiento.',
          },
        ],
      },
    },
    social: {
      title: 'Sígueme en LinkedIn',
      subtitle: 'Comparto mi trayectoria profesional. Consulta mi perfil para ver las últimas publicaciones.',
      viewProfile: 'Ver perfil completo',
    },
    contact: {
      title: ['¿Hablamos?', ''],
      subtitle: 'Elige la mejor forma de conectarnos. Estoy disponible para propuestas y colaboraciones.',
      emailLabel: 'E-mail',
      whatsappLabel: 'Whatsapp',
      sendEmail: 'Enviar Email',
      scheduleMeeting: 'Agendar Reunión',
      form: {
        emailField: 'Email',
        subjectField: 'Asunto',
        subjectPlaceholder: 'Asunto del contacto',
        messageField: 'Tu Mensaje',
        messagePlaceholder: 'Hola, me gustaría hablar sobre...',
        submitIdle: 'Enviar ahora',
        submitLoading: 'Enviando...',
        submitSuccess: '¡Enviado!',
        successMsg: '¡Mensaje enviado con éxito! Pronto me pondré en contacto.',
        errorMsg: 'Ocurrió un error al enviar. Inténtalo de nuevo o usa WhatsApp.',
      },
      meeting: {
        title: 'Agenda una conversación',
        subtitle: 'Contáctame por WhatsApp o email para encontrar un horario que funcione para los dos.',
        whatsappBtn: 'WhatsApp',
        emailBtn: 'Enviar Email',
      },
    },
    footer: {
      rights: '© 2026 - Todos los derechos reservados Pedro Soares.',
      privacy: 'Privacidad',
      terms: 'Términos',
    },
    projectsPage: {
      title: 'Todos los Proyectos',
      subtitle: 'Una selección completa de mis trabajos y proyectos desarrollados.',
      back: 'Volver al inicio',
      empty: 'Aún no hay proyectos registrados.',
    },
  },
};

// ─── SVG FLAGS (cross-platform, sem dependência de emoji do sistema) ──────────
const FlagBR = () => (
  <svg width="20" height="14" viewBox="0 0 20 14" style={{ borderRadius: 2, display: 'block', flexShrink: 0 }}>
    <rect width="20" height="14" fill="#009C3B"/>
    <polygon points="10,1.5 18.5,7 10,12.5 1.5,7" fill="#FEDF00"/>
    <circle cx="10" cy="7" r="3.2" fill="#002776"/>
    <path d="M6.9 8.2 Q10 5.8 13.1 8.2" stroke="white" strokeWidth="0.8" fill="none"/>
  </svg>
);

const FlagUS = () => (
  <svg width="20" height="14" viewBox="0 0 20 14" style={{ borderRadius: 2, display: 'block', flexShrink: 0 }}>
    <rect width="20" height="14" fill="#B22234"/>
    <rect y="1.08" width="20" height="1.08" fill="white"/>
    <rect y="3.23" width="20" height="1.08" fill="white"/>
    <rect y="5.38" width="20" height="1.08" fill="white"/>
    <rect y="7.54" width="20" height="1.08" fill="white"/>
    <rect y="9.69" width="20" height="1.08" fill="white"/>
    <rect y="11.85" width="20" height="1.08" fill="white"/>
    <rect width="8" height="7.54" fill="#3C3B6E"/>
  </svg>
);

const FlagES = () => (
  <svg width="20" height="14" viewBox="0 0 20 14" style={{ borderRadius: 2, display: 'block', flexShrink: 0 }}>
    <rect width="20" height="14" fill="#c60b1e"/>
    <rect y="3.5" width="20" height="7" fill="#ffc400"/>
    <rect y="3.5" width="20" height="1.2" fill="#c60b1e"/>
    <rect y="9.3" width="20" height="1.2" fill="#c60b1e"/>
  </svg>
);

// ─── IDIOMAS DISPONÍVEIS ──────────────────────────────────────────────────────
const LANGUAGES = [
  { code: 'pt', label: 'PT', Flag: FlagBR },
  { code: 'en', label: 'EN', Flag: FlagUS },
  { code: 'es', label: 'ES', Flag: FlagES },
];

// ─── PROJETOS ─────────────────────────────────────────────────────────────────
// Fonte única de todos os projetos.
//  - featured: true  → aparece na home (mantenha apenas 2 favoritos)
//  - title/description: objeto com as 3 traduções { pt, en, es }
//  - tags: tecnologias exibidas no card (opcional)
//  - link: URL do projeto (opcional)
// Para adicionar um projeto novo, basta copiar um bloco abaixo e preencher.
const PROJECTS = [
  {
    id: 'aksor',
    featured: false,
    company: 'Aksor',
    period: '2025',
    link: 'https://aksor.com.br',
    tags: ['React', 'Node.js', 'QR Code'],
    title: {
      pt: 'Aksor - Sistema de Gerenciamento de Presença',
      en: 'Aksor - Attendance Management System',
      es: 'Aksor - Sistema de Gestión de Asistencia',
    },
    description: {
      pt: 'Plataforma web que permite a criação de eventos acadêmicos, cadastro de participantes e controle automático de presença via QR Code, eliminando processos manuais e reduzindo erros de validação. Redução média de 4min23s no tempo de chamada e redução significativa de erros (85,7%).',
      en: 'Web platform that enables creation of academic events, participant registration and automatic attendance tracking via QR Code, eliminating manual processes and reducing validation errors. Average reduction of 4min23s in roll-call time and significant error reduction (85.7%).',
      es: 'Plataforma web que permite la creación de eventos académicos, registro de participantes y control automático de asistencia mediante QR Code, eliminando procesos manuales y reduciendo errores de validación. Reducción promedio de 4min23s en el tiempo de llamada y reducción significativa de errores (85,7%).',
    },
  },

  {
    id: 'deteccao-afogamento',
    featured: true,
    company: 'TCC - Uniamérica',
    period: '2026',
    link: 'https://github.com/SoaresPedroBit/DeteccaoAfogamento-DrowningAlert',
    tags: ['Python', 'YOLO', 'PyTorch', 'Visão Computacional'],
    title: {
      pt: 'Detecção de Afogamento em Piscinas',
      en: 'Drowning Detection in Swimming Pools',
      es: 'Detección de Ahogamiento en Piscinas',
    },
    description: {
      pt: 'Trabalho de Conclusão de Curso que utiliza modelos de visão computacional YOLO para detectar situações de afogamento em piscinas a partir da análise de imagens. O sistema identifica quatro classes (piscina, adulto seguro, criança segura e afogamento), comparando diferentes modelos YOLO treinados sobre um dataset de mais de 7.000 imagens.',
      en: 'Final undergraduate thesis project that uses YOLO computer vision models to detect drowning situations in swimming pools through image analysis. The system identifies four classes (pool, safe adult, safe child and drowning), comparing different YOLO models trained on a dataset of over 7,000 images.',
      es: 'Trabajo de Conclusión de Curso que utiliza modelos de visión computacional YOLO para detectar situaciones de ahogamiento en piscinas a partir del análisis de imágenes. El sistema identifica cuatro clases (piscina, adulto seguro, niño seguro y ahogamiento), comparando diferentes modelos YOLO entrenados sobre un dataset de más de 7.000 imágenes.',
    },
  },

  {
    id: 'girocerto',
    featured: true,
    company: 'GiroCerto',
    period: '2026',
    link: '/girocerto',
    tags: ['Android', 'Capacitor', 'JavaScript', 'Firebase Analytics'],
    title: {
      pt: 'GiroCerto - Controle de Veículos para Entregadores',
      en: 'GiroCerto - Vehicle Tracking for Delivery Riders',
      es: 'GiroCerto - Control de Vehículos para Repartidores',
    },
    description: {
      pt: 'Aplicativo Android que reúne ganhos, combustível, manutenção e quilometragem em um só painel, calculando lucro do dia, consumo (km/l), custo por km e ganho por km. Funciona totalmente offline — os dados ficam no aparelho do usuário. A página do projeto traz a landing e uma demo interativa que roda no navegador.',
      en: 'Android app that brings earnings, fuel, maintenance and mileage into a single dashboard, calculating daily profit, fuel efficiency (km/l), cost per km and earnings per km. Works fully offline — data stays on the user’s device. The project page includes the landing and an interactive demo that runs in the browser.',
      es: 'Aplicación Android que reúne ganancias, combustible, mantenimiento y kilometraje en un solo panel, calculando el beneficio del día, consumo (km/l), coste por km y ganancia por km. Funciona totalmente sin conexión — los datos permanecen en el dispositivo del usuario. La página del proyecto incluye la landing y una demo interactiva que funciona en el navegador.',
    },
  },

  // ⬇️ Para adicionar um projeto novo, copie um dos blocos acima e ajuste os campos.
  //    Mantenha apenas 2 com featured:true (aparecem na home).
];

const ROUTE_PROJECTS = '#/projetos';

// ─── API ──────────────────────────────────────────────────────────────────────
const enviarEmail = async (email, assunto, mensagem) => {
  const response = await fetch('https://api.soarespedro.com.br/enviarEmail', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, assunto, mensagem }),
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return response;
};

// ─── LANGUAGE SELECTOR COMPONENT ─────────────────────────────────────────────
const LanguageSelector = ({ lang, setLang, accent }) => {
  const [open, setOpen] = useState(false);
  const current = LANGUAGES.find((l) => l.code === lang);
  const CurrentFlag = current.Flag;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-700 hover:border-gray-500 transition-all text-sm font-bold"
        style={{ color: '#BFC3C7' }}
      >
        <Globe size={14} style={{ color: accent }} />
        <CurrentFlag />
        <span>{current.label}</span>
        <ChevronDown
          size={12}
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 rounded-xl border border-gray-700 overflow-hidden shadow-2xl z-50"
            style={{ backgroundColor: '#1a1d23', minWidth: '120px' }}
          >
            {LANGUAGES.map((l) => {
              const ItemFlag = l.Flag;
              return (
                <button
                  key={l.code}
                  onClick={() => { setLang(l.code); setOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium hover:bg-gray-700/50 transition-colors text-left"
                  style={{
                    color: lang === l.code ? accent : '#BFC3C7',
                    borderLeft: lang === l.code ? `3px solid ${accent}` : '3px solid transparent',
                  }}
                >
                  <ItemFlag />
                  <span>{l.label}</span>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── PROJECT CARD ─────────────────────────────────────────────────────────────
const ProjectCard = ({ project, lang, t, colors }) => {
  const [hovered, setHovered] = useState(false);

  return (
  <motion.div
    onMouseEnter={() => setHovered(true)}
    onMouseLeave={() => setHovered(false)}
    whileHover={{ y: -4 }}
    transition={{ duration: 0.2 }}
    className="group relative flex flex-col p-6 rounded-2xl border border-gray-800 hover:border-orange-500/50 transition-colors duration-300 h-full"
    style={{ backgroundColor: '#252932' }}
  >
    <div className="flex items-start justify-between gap-4 mb-4">
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center border border-gray-700 group-hover:bg-orange-500 transition-all shrink-0"
        style={{ color: hovered ? '#000' : colors.accent }}
      >
        <Code size={22} />
      </div>
      <span
        className="text-xs font-semibold px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/30 shrink-0"
        style={{ color: colors.accent }}
      >
        {project.period}
      </span>
    </div>

    <h3 className="text-xl font-bold text-white mb-1">{project.title[lang]}</h3>
    <h4 className="text-sm font-medium mb-3" style={{ color: colors.lightGray }}>{project.company}</h4>
    <p className="text-sm leading-relaxed flex-grow" style={{ color: colors.mutedGray }}>
      {project.description[lang]}
    </p>

    {project.tags && project.tags.length > 0 && (
      <div className="flex flex-wrap gap-2 mt-4">
        {project.tags.map((tag) => (
          <span key={tag} className="text-[11px] font-medium px-2.5 py-1 rounded-md bg-black/30 text-gray-400">
            {tag}
          </span>
        ))}
      </div>
    )}

    {project.link && (
      <a
        href={project.link}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-500 hover:gap-2.5 transition-all mt-5 w-fit"
      >
        {t.experience.viewProject}
        <ArrowUpRight size={16} />
      </a>
    )}
  </motion.div>
  );
};

// ─── ALL PROJECTS PAGE (rota #/projetos, abre em nova aba) ────────────────────
const AllProjectsPage = ({ lang, setLang, t, colors, onBack }) => (
  <div className="min-h-screen font-sans text-white selection:bg-orange-500/30" style={{ backgroundColor: colors.bg }}>
    {/* NAVBAR */}
    <nav className="sticky top-0 left-0 right-0 z-50 px-4 md:px-10 py-4">
      <div
        className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-8 py-4 bg-opacity-90 backdrop-blur-md border-b border-gray-800"
        style={{ backgroundColor: '#1a1d23', borderBottomLeftRadius: '10px', borderBottomRightRadius: '10px' }}
      >
        <button onClick={onBack} className="text-2xl font-bold tracking-tighter" style={{ color: colors.accent }}>
          SOARES<span className="text-white">.</span>
        </button>
        <div className="flex items-center gap-3 md:gap-6">
          <button
            onClick={onBack}
            className="hidden sm:flex items-center gap-2 text-sm font-medium hover:opacity-80 transition-opacity"
            style={{ color: colors.lightGray }}
          >
            <ArrowLeft size={16} />
            {t.projectsPage.back}
          </button>
          <LanguageSelector lang={lang} setLang={setLang} accent={colors.accent} />
        </div>
      </div>
    </nav>

    {/* CONTEÚDO */}
    <section className="pt-16 pb-24 px-6 md:px-20 max-w-7xl mx-auto">
      <button
        onClick={onBack}
        className="sm:hidden flex items-center gap-2 text-sm font-medium mb-8 hover:opacity-80 transition-opacity"
        style={{ color: colors.lightGray }}
      >
        <ArrowLeft size={16} />
        {t.projectsPage.back}
      </button>

      <AnimatePresence mode="wait">
        <motion.div
          key={`proj-page-head-${lang}`}
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t.projectsPage.title}</h1>
          <div className="w-20 h-1 rounded-full mb-6" style={{ backgroundColor: colors.accent }} />
          <p className="text-lg max-w-2xl" style={{ color: colors.mutedGray }}>{t.projectsPage.subtitle}</p>
        </motion.div>
      </AnimatePresence>

      {PROJECTS.length === 0 ? (
        <p style={{ color: colors.mutedGray }}>{t.projectsPage.empty}</p>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={`proj-grid-${lang}`}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {PROJECTS.map((project) => (
              <ProjectCard key={project.id} project={project} lang={lang} t={t} colors={colors} />
            ))}
          </motion.div>
        </AnimatePresence>
      )}
    </section>
  </div>
);

// ─── APP ──────────────────────────────────────────────────────────────────────
const App = () => {
  const [lang, setLang] = useState('pt');
  const [activeTab, setActiveTab] = useState('profissional');
  const [contactMode, setContactMode] = useState('email');
  const [email, setEmail] = useState('');
  const [assunto, setAssunto] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [sendStatus, setSendStatus] = useState('idle');
  const [route, setRoute] = useState(window.location.hash);

  const t = translations[lang];

  // Roteamento simples por hash: #/projetos → página "Todos os Projetos"
  useEffect(() => {
    const onHashChange = () => setRoute(window.location.hash);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const goHome = () => {
    window.location.hash = '';
    setRoute('');
    window.scrollTo(0, 0);
  };

  const featuredProjects = PROJECTS.filter((p) => p.featured);

  const colors = {
    bg: '#0F1115',
    lightGray: '#BFC3C7',
    mutedGray: '#8C9096',
    accent: '#F47C20',
  };

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const shadowHeight = useTransform(scrollYProgress, [0, 0.5], ['0%', '100%']);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSendStatus('loading');
    try {
      await enviarEmail(email, assunto, mensagem);
      setSendStatus('success');
      setEmail(''); setAssunto(''); setMensagem('');
      setTimeout(() => setSendStatus('idle'), 4000);
    } catch (err) {
      console.error(err);
      setSendStatus('error');
      setTimeout(() => setSendStatus('idle'), 4000);
    }
  };

  const experienceData = t.experience.data;

  // Rota da página de todos os projetos (aberta em nova aba)
  if (route === ROUTE_PROJECTS) {
    return <AllProjectsPage lang={lang} setLang={setLang} t={t} colors={colors} onBack={goHome} />;
  }

  return (
    <div className="min-h-screen font-sans text-white selection:bg-orange-500/30" style={{ backgroundColor: colors.bg }}>

      {/* NAVBAR */}
      <nav className="sticky top-0 left-0 right-0 z-50 px-4 md:px-10 py-4">
        <div
          className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-8 py-4 bg-opacity-90 backdrop-blur-md border-b border-gray-800"
          style={{ backgroundColor: '#1a1d23', borderBottomLeftRadius: '10px', borderBottomRightRadius: '10px' }}
        >
          <div className="text-2xl font-bold tracking-tighter" style={{ color: colors.accent }}>
            SOARES<span className="text-white">.</span>
          </div>

          <div className="hidden md:flex items-center space-x-8 text-sm font-medium">
            <a href="#sobre" className="hover:opacity-80 transition-opacity" style={{ color: colors.lightGray }}>{t.nav.about}</a>
            <a href="#experiencia" className="hover:opacity-80 transition-opacity" style={{ color: colors.lightGray }}>{t.nav.experience}</a>
            <a href="#sociais" className="hover:opacity-80 transition-opacity" style={{ color: colors.lightGray }}>{t.nav.social}</a>
            <LanguageSelector lang={lang} setLang={setLang} accent={colors.accent} />
            <a
              href="#contato"
              className="px-6 py-2 rounded-full font-bold transition-all hover:scale-105 active:scale-95"
              style={{ backgroundColor: colors.accent, color: colors.bg }}
            >
              {t.nav.contact}
            </a>
          </div>

          <div className="md:hidden flex items-center gap-3">
            <LanguageSelector lang={lang} setLang={setLang} accent={colors.accent} />
            <div style={{ color: colors.accent }}><MessageCircle size={26} /></div>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section id="sobre" ref={heroRef} className="pt-40 pb-20 px-6 md:px-20 max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center min-h-screen">
        <div>
          <AnimatePresence mode="wait">
            <motion.h2
              key={`label-${lang}`}
              initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.25 }}
              className="text-sm font-bold uppercase tracking-widest mb-4"
              style={{ color: colors.accent }}
            >
              {t.hero.label}
            </motion.h2>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            <motion.h1
              key={`title-${lang}`}
              initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.3 }}
              className="text-5xl md:text-7xl font-bold mb-6 leading-tight"
            >
              {t.hero.title[0]} <br /> {t.hero.title[1]}
            </motion.h1>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            <motion.p
              key={`bio-${lang}`}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="text-lg leading-relaxed max-w-lg"
              style={{ color: colors.mutedGray }}
            >
              {t.hero.bio}
            </motion.p>
          </AnimatePresence>
        </div>

        <div className="relative group h-[500px] rounded-2xl overflow-hidden shadow-2xl border border-gray-800">
          <img src="portifolio.jpg" alt="Profile" className="w-full h-full object-cover" />
          <motion.div
            style={{
              height: shadowHeight,
              background: `linear-gradient(to top, ${colors.bg} 0%, rgba(15,17,21,0.8) 50%, transparent 100%)`,
            }}
            className="absolute bottom-0 left-0 right-0 z-10 pointer-events-none"
          />
          <div className="absolute bottom-10 left-10 z-20">
            <div className="px-4 py-2 bg-black bg-opacity-50 backdrop-blur-md rounded-lg border border-white/10">
              <p className="text-sm font-medium" style={{ color: colors.lightGray }}>{t.hero.available}</p>
            </div>
          </div>
        </div>
      </section>

      {/* EXPERIÊNCIA */}
      <section id="experiencia" className="py-20 px-6 md:px-20 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <AnimatePresence mode="wait">
            <motion.h2
              key={`exp-title-${lang}`}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="text-4xl font-bold mb-4"
            >
              {t.experience.title}
            </motion.h2>
          </AnimatePresence>
          <div className="w-20 h-1 mx-auto rounded-full" style={{ backgroundColor: colors.accent }} />
        </div>

        <div className="mt-12">
          <div className="flex flex-wrap md:flex-nowrap gap-1">
            {['profissional', 'academicos', 'projetos'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`relative px-8 py-3 text-sm font-bold uppercase tracking-wider rounded-t-xl transition-all duration-300 ${
                  activeTab === tab ? 'z-20' : 'z-10 mt-2 opacity-60 hover:opacity-100'
                }`}
                style={{
                  backgroundColor: activeTab === tab ? '#1a1d23' : '#252932',
                  borderTop: activeTab === tab ? `3px solid ${colors.accent}` : 'none',
                }}
              >
                <div className="flex items-center gap-2">
                  {tab === 'profissional' && <Briefcase size={16} />}
                  {tab === 'academicos' && <GraduationCap size={16} />}
                  {tab === 'projetos' && <Code size={16} />}
                  {t.experience.tabs[tab]}
                </div>
              </button>
            ))}
          </div>

          <div className="p-8 md:p-12 rounded-b-2xl rounded-tr-2xl border border-gray-800 shadow-xl" style={{ backgroundColor: '#1a1d23' }}>
            <AnimatePresence mode="wait">
              {activeTab === 'projetos' ? (
                <motion.div
                  key={`projetos-${lang}`}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {featuredProjects.map((project) => (
                      <ProjectCard key={project.id} project={project} lang={lang} t={t} colors={colors} />
                    ))}
                  </div>

                  <div className="flex justify-center mt-10">
                    <a
                      href={ROUTE_PROJECTS}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-bold transition-all hover:scale-105 active:scale-95"
                      style={{ backgroundColor: colors.accent, color: colors.bg }}
                    >
                      {t.experience.viewAll}
                      <ArrowUpRight size={18} />
                    </a>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key={`${activeTab}-${lang}`}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="grid gap-8"
                >
                  {experienceData[activeTab].map((item, idx) => (
                    <div key={idx} className="group relative pl-8 border-l-2 border-gray-700 hover:border-orange-500 transition-all duration-300">
                      <div className="absolute -left-2 top-1 w-4 h-4 rounded-full bg-gray-700 group-hover:bg-orange-500 transition-colors border-4 border-[#1a1d23]" />
                      <div className="flex items-start justify-between gap-4">
                        <h3 className="text-2xl font-bold text-white">{item.title}</h3>
                        <span
                          className="text-xs font-semibold px-4 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30 shrink-0"
                          style={{ color: colors.accent }}
                        >
                          {item.period}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-2">
                        <h4 className="text-lg font-medium" style={{ color: colors.lightGray }}>{item.company}</h4>
                        {item.link && (
                          <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-sm text-orange-500 hover:underline">
                            {t.experience.viewProject}
                          </a>
                        )}
                      </div>
                      <p className="mt-4 whitespace-pre-line" style={{ color: colors.mutedGray }}>{item.description}</p>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* SOCIAIS */}
      <section id="sociais" className="py-20 px-6 md:px-20 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-end justify-between mb-12 gap-6">
          <div className="max-w-xl text-left">
            <AnimatePresence mode="wait">
              <motion.h2
                key={`soc-title-${lang}`}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="text-4xl font-bold mb-4"
              >
                {t.social.title}
              </motion.h2>
            </AnimatePresence>
            <AnimatePresence mode="wait">
              <motion.p
                key={`soc-sub-${lang}`}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ color: colors.mutedGray }}
              >
                {t.social.subtitle}
              </motion.p>
            </AnimatePresence>
          </div>
          <a
            href="https://www.linkedin.com/in/pedroo-soares/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-6 py-3 rounded-xl border border-gray-700 hover:bg-gray-800 transition-all shrink-0"
            style={{ color: colors.lightGray }}
          >
            <Linkedin size={20} />
            {t.social.viewProfile}
          </a>
        </div>
      </section>

      {/* CONTATO */}
      <section id="contato" className="py-20 px-6 md:px-20 max-w-7xl mx-auto mb-20">
        <div className="bg-[#1a1d23] rounded-3xl overflow-hidden border border-gray-800 shadow-2xl">
          <div className="grid grid-cols-1 md:grid-cols-5 h-full">

            <div className="md:col-span-2 p-10 md:p-16 flex flex-col justify-between" style={{ backgroundColor: '#252932' }}>
              <div>
                <AnimatePresence mode="wait">
                  <motion.h2
                    key={`ct-title-${lang}`}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="text-4xl font-bold mb-6"
                  >
                    {t.contact.title[0]} <br />{t.contact.title[1]}
                  </motion.h2>
                </AnimatePresence>
                <AnimatePresence mode="wait">
                  <motion.p
                    key={`ct-sub-${lang}`}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="mb-12" style={{ color: colors.mutedGray }}
                  >
                    {t.contact.subtitle}
                  </motion.p>
                </AnimatePresence>

                <div className="space-y-6">
                  <div className="flex items-center gap-4 group cursor-pointer">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center transition-all group-hover:bg-orange-500 group-hover:text-black border border-gray-700" style={{ color: colors.accent }}>
                      <Mail size={24} />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase opacity-50">{t.contact.emailLabel}</p>
                      <p className="font-medium">soarespedroo357@gmail.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 group cursor-pointer">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center transition-all group-hover:bg-green-500 group-hover:text-black border border-gray-700" style={{ color: colors.accent }}>
                      <MessageCircle size={24} />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase opacity-50">{t.contact.whatsappLabel}</p>
                      <p className="font-medium">+55 (45) 9 9848-0913</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 mt-12">
                <a href="https://github.com/SoaresPedroBit" className="p-3 bg-[#1a1d23] rounded-full hover:scale-110 transition-transform"><Github size={20} /></a>
                <a href="https://www.linkedin.com/in/pedroo-soares/" className="p-3 bg-[#1a1d23] rounded-full hover:scale-110 transition-transform"><Linkedin size={20} /></a>
                <a href="https://www.instagram.com/peeh_soares18?igsh=YWI0ZTFiNzNrZ3My" className="p-3 bg-[#1a1d23] rounded-full hover:scale-110 transition-transform"><Instagram size={20} /></a>
              </div>
            </div>

            <div className="md:col-span-3 p-8 md:p-16">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`contact-tabs-${lang}`}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="flex gap-4 mb-10 p-1 bg-black/20 rounded-xl w-fit"
                >
                  <button
                    onClick={() => setContactMode('email')}
                    className={`px-6 py-2 rounded-lg font-bold transition-all ${contactMode === 'email' ? 'text-black shadow-lg shadow-orange-500/20' : 'text-gray-400 hover:text-white'}`}
                    style={{ backgroundColor: contactMode === 'email' ? colors.accent : '' }}
                  >
                    {t.contact.sendEmail}
                  </button>
                  <button
                    onClick={() => setContactMode('meeting')}
                    className={`px-6 py-2 rounded-lg font-bold transition-all ${contactMode === 'meeting' ? 'text-black shadow-lg shadow-orange-500/20' : 'text-gray-400 hover:text-white'}`}
                    style={{ backgroundColor: contactMode === 'meeting' ? colors.accent : '' }}
                  >
                    {t.contact.scheduleMeeting}
                  </button>
                </motion.div>
              </AnimatePresence>

              <AnimatePresence mode="wait">
                {contactMode === 'email' ? (
                  <motion.div
                    key={`email-form-${lang}`}
                    initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    <AnimatePresence>
                      {sendStatus === 'success' && (
                        <motion.div
                          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                          className="flex items-center gap-3 px-4 py-3 rounded-xl bg-green-500/10 border border-green-500/30 text-green-400"
                        >
                          <CheckCircle size={18} />
                          <span className="text-sm font-medium">{t.contact.form.successMsg}</span>
                        </motion.div>
                      )}
                      {sendStatus === 'error' && (
                        <motion.div
                          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                          className="flex items-center gap-3 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400"
                        >
                          <AlertCircle size={18} />
                          <span className="text-sm font-medium">{t.contact.form.errorMsg}</span>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <form onSubmit={handleSubmit}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <label className="block text-xs font-bold uppercase mb-2 opacity-60">{t.contact.form.emailField}</label>
                          <input
                            type="email" required
                            className="w-full bg-gray-800 border-none rounded-xl p-4 focus:ring-2 focus:ring-orange-500 outline-none"
                            placeholder="seu@email.com"
                            value={email} onChange={(e) => setEmail(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold uppercase mb-2 opacity-60">{t.contact.form.subjectField}</label>
                          <input
                            type="text" required
                            className="w-full bg-gray-800 border-none rounded-xl p-4 focus:ring-2 focus:ring-orange-500 outline-none"
                            placeholder={t.contact.form.subjectPlaceholder}
                            value={assunto} onChange={(e) => setAssunto(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="relative mb-6">
                        <label className="block text-xs font-bold uppercase mb-2 opacity-60">{t.contact.form.messageField}</label>
                        <textarea
                          required
                          value={mensagem} onChange={(e) => setMensagem(e.target.value)}
                          className="w-full bg-gray-800 border-none rounded-xl p-4 h-32 focus:ring-2 focus:ring-orange-500 outline-none resize-none"
                          placeholder={t.contact.form.messagePlaceholder}
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={sendStatus === 'loading' || sendStatus === 'success'}
                        className="w-full py-4 rounded-xl font-bold uppercase tracking-widest transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        style={{ backgroundColor: colors.accent, color: colors.bg }}
                      >
                        {sendStatus === 'loading' && <Loader2 size={18} className="animate-spin" />}
                        {sendStatus === 'loading'
                          ? t.contact.form.submitLoading
                          : sendStatus === 'success'
                          ? t.contact.form.submitSuccess
                          : t.contact.form.submitIdle}
                      </button>
                    </form>
                  </motion.div>
                ) : (
                  <motion.div
                    key={`meeting-${lang}`}
                    initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                  >
                    <div
                      className="p-8 bg-gray-800/30 rounded-2xl border border-white/5 flex flex-col items-center justify-center gap-6 text-center"
                      style={{ minHeight: '300px' }}
                    >
                      <div className="w-16 h-16 rounded-2xl flex items-center justify-center border border-gray-700" style={{ color: colors.accent }}>
                        <Calendar size={32} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2">{t.contact.meeting.title}</h3>
                        <p className="text-sm mb-6" style={{ color: colors.mutedGray }}>{t.contact.meeting.subtitle}</p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm">
                        <a
                          href="https://wa.me/5545998480913"
                          target="_blank" rel="noopener noreferrer"
                          className="flex-1 py-3 px-4 rounded-xl font-bold text-sm text-center transition-all hover:brightness-110"
                          style={{ backgroundColor: colors.accent, color: colors.bg }}
                        >
                          {t.contact.meeting.whatsappBtn}
                        </a>
                        <button
                          onClick={() => setContactMode('email')}
                          className="flex-1 py-3 px-4 rounded-xl font-bold text-sm border border-gray-700 hover:bg-gray-800 transition-all"
                          style={{ color: colors.lightGray }}
                        >
                          {t.contact.meeting.emailBtn}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 border-t border-gray-800 text-center px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-xl font-bold" style={{ color: colors.accent }}>
            SOARES<span className="text-white">.</span>
          </div>
          <AnimatePresence mode="wait">
            <motion.p
              key={`footer-${lang}`}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="text-sm opacity-50" style={{ color: colors.lightGray }}
            >
              {t.footer.rights}
            </motion.p>
          </AnimatePresence>
          <div className="flex gap-8 text-sm font-medium">
            <button className="hover:text-orange-500 transition-colors">{t.footer.privacy}</button>
            <button className="hover:text-orange-500 transition-colors">{t.footer.terms}</button>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default App;